import React, { createContext, useContext, useState, useCallback } from 'react';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [modal, setModal] = useState(null);

  // Generate unique ID for toasts
  const generateId = () => `toast-${Date.now()}-${Math.random()}`;

  // Remove a toast
  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Add a toast notification
  const addToast = useCallback((type, title, description, duration = 5000, action = null) => {
    const id = generateId();
    const toast = { id, type, title, description, action };
    
    setToasts(prev => [...prev, toast]);

    // Auto-dismiss
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, [removeToast]);

  // Notification helpers
  const notifySuccess = useCallback((title, description, duration, action) => {
    return addToast('success', title, description, duration, action);
  }, [addToast]);

  const notifyError = useCallback((title, description, duration, action) => {
    return addToast('error', title, description, duration, action);
  }, [addToast]);

  const notifyWarning = useCallback((title, description, duration, action) => {
    return addToast('warning', title, description, duration, action);
  }, [addToast]);

  const notifyInfo = useCallback((title, description, duration, action) => {
    return addToast('info', title, description, duration, action);
  }, [addToast]);

  const notify = useCallback((title, description, duration, action) => {
    return addToast('neutral', title, description, duration, action);
  }, [addToast]);

  // Modal helpers
  const openConfirmModal = useCallback((config) => {
    return new Promise((resolve) => {
      setModal({
        ...config,
        onConfirm: () => {
          if (config.onConfirm) config.onConfirm();
          setModal(null);
          resolve(true);
        },
        onCancel: () => {
          if (config.onCancel) config.onCancel();
          setModal(null);
          resolve(false);
        }
      });
    });
  }, []);

  const closeModal = useCallback(() => {
    setModal(null);
  }, []);

  const value = {
    toasts,
    removeToast,
    notifySuccess,
    notifyError,
    notifyWarning,
    notifyInfo,
    notify,
    modal,
    openConfirmModal,
    closeModal
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

